import { TextSelection } from 'prosemirror-state';
import { startOfBlock } from '../../helpers/selection';
import { isBlank, isEmpty, isPresent } from '@ember/utils';
import { findParentNodeOfType } from 'prosemirror-utils';
import { getCollapsibleSectionChildPos, parentIsCollapsibleSectionContent, parentIsCollapsibleSectionSummary } from '../../helpers/collapsible-section';
/**
 * When pressing backspace on the last empty text node in a collapsibleSectionContent Node,
 * the empty text node should be lifted to the top-level document.
 * This is how the teammate "exits" the collapsible section content area.
 */
export function liftBlockFromCollapsibleSection(state, dispatch) {
  if (!shouldLiftBlock(state)) {
    return false;
  }
  let {
    selection,
    tr
  } = state;
  let selectionRange = selection.$from.blockRange(selection.$to);
  if (selectionRange && dispatch) {
    tr.lift(selectionRange, 0);
    dispatch(tr);
    return true;
  }
  return false;
}

/**
 * When pressing backspace on the first text node in a collapsibleSectionContent Node,
 * the text node should be joined with the text node in collapsibleSectionSummary.
 *
 * Before:
 * > This is a summary\
 *   |Blah\
 *   Blah
 *
 * After:
 * > This is a summary|Blah\
 *   Blah
 */
export function joinBlocksFromCollapsibleSection(state, dispatch) {
  if (!shouldJoinBlocks(state)) {
    return false;
  }
  let {
    schema,
    selection,
    tr
  } = state;
  let collapsibleSection = findParentNodeOfType(schema.nodes.collapsibleSection)(selection);
  if (!collapsibleSection) {
    return false;
  }
  let collapsibleSectionSummaryPos = getCollapsibleSectionChildPos(state, collapsibleSection, schema.nodes.collapsibleSectionSummary);
  let textNode = state.selection.$from.parent;
  let summaryTextNode = collapsibleSectionSummaryPos.nodeAfter;
  let firstSlice = summaryTextNode?.slice(0, summaryTextNode.content.size);
  let secondSlice = textNode.slice(0, textNode.content.size);
  let newTextNode = summaryTextNode?.type.createAndFill(summaryTextNode.attrs, firstSlice?.content.append(secondSlice.content));
  let $anchor = state.selection.$anchor;
  let newAnchorPosition = collapsibleSectionSummaryPos.start() + (summaryTextNode?.content.size || 0) + 1;
  if (dispatch && newTextNode && summaryTextNode) {
    tr = tr.deleteRange($anchor.pos, $anchor.pos + textNode.nodeSize).replaceWith(collapsibleSectionSummaryPos.start(), collapsibleSectionSummaryPos.start() + summaryTextNode.nodeSize, newTextNode);
    let textSelection = TextSelection.create(tr.doc, newAnchorPosition);
    tr.setSelection(textSelection).scrollIntoView();
    dispatch(tr);
    return true;
  }
  return false;
}

/**
 * Check if anchor is at the start of collapsibleSectionSummary Node.
 * If so, the collapsible section is deleted and if there's any content in it,
 * it gets lifted to top-level document.
 *
 * Before:
 * > |This is a summary\
 *   Blah
 *
 *
 * After:\
 * |This is a summary\
 * Blah
 */
export function deleteCollapsibleSection(state, dispatch) {
  if (!shouldDeleteCollapsibleSection(state)) {
    return false;
  }
  let {
    schema,
    selection,
    tr
  } = state;
  let collapsibleSection = findParentNodeOfType(schema.nodes.collapsibleSection)(selection);
  if (!collapsibleSection) {
    return false;
  }
  let newContent = extractCollapsibleSectionContentNodes(collapsibleSection);
  let collapsibleSectionStart = collapsibleSection.pos;
  let collapsibleSectionEnd = collapsibleSection.pos + collapsibleSection?.node.nodeSize;
  if (dispatch && newContent) {
    tr = tr.replaceWith(collapsibleSectionStart, collapsibleSectionEnd, newContent);
    let textSelection = TextSelection.create(tr.doc, collapsibleSectionStart + 1);
    tr.setSelection(textSelection).scrollIntoView();
    dispatch(tr);
    return true;
  }
  return false;
}
function shouldDeleteCollapsibleSection(state) {
  if (!startOfBlock(state)) {
    return false;
  }
  let {
    schema,
    selection
  } = state;
  let collapsibleSection = findParentNodeOfType(schema.nodes.collapsibleSection)(selection);
  return parentIsCollapsibleSectionSummary(state) && isPresent(collapsibleSection);
}
function shouldLiftBlock(state) {
  if (!startOfBlock(state)) {
    return false;
  }
  return parentIsCollapsibleSectionContent(state) && isLastEmptyTextNode(state.selection);
}
function shouldJoinBlocks(state) {
  if (!startOfBlock(state)) {
    return false;
  }
  let {
    doc
  } = state;
  // $anchor points to where the paragraph content starts
  // positionBefore points to where the paragraph Node starts ($anchor - 1)
  let $anchor = state.selection.$anchor;
  let positionBefore = doc.resolve($anchor.before());
  let isFirstTextNode = isBlank(positionBefore.nodeBefore);
  return parentIsCollapsibleSectionContent(state) && isFirstTextNode;
}
function isLastEmptyTextNode(selection) {
  let $anchor = selection.$anchor;
  let positionAfter = $anchor.doc.resolve($anchor.after());
  let textBlock = selection.$from.parent;
  return isBlank(positionAfter.nodeAfter) && isEmpty(textBlock.content);
}
function extractCollapsibleSectionContentNodes(collapsibleSection) {
  let collapsibleSectionSummary = collapsibleSection.node.firstChild;
  let collapsibleSectionContent = collapsibleSection.node.lastChild;
  if (!collapsibleSectionSummary || !collapsibleSectionContent) {
    return null;
  }
  return collapsibleSectionSummary.content.append(collapsibleSectionContent.content);
}